import React, { forwardRef } from 'react';
import { cn } from '../../../utils/cn';

export type InputSize = 'sm' | 'md' | 'lg';

export interface InputProps extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'size'> {
  size?: InputSize;
  label?: string;
  helperText?: string;
  errorText?: string;
  leftIcon?: React.ReactNode;
  rightIcon?: React.ReactNode;
  fullWidth?: boolean;
  required?: boolean;
}

const Input = forwardRef<HTMLInputElement, InputProps>(
  (
    {
      size = 'md',
      label,
      helperText,
      errorText,
      leftIcon,
      rightIcon,
      fullWidth = false,
      required = false,
      className,
      id,
      type,
      onFocus,
      onBlur,
      onWheel,
      ...props
    },
    ref
  ) => {
    const inputId = id || `input-${Math.random().toString(36).substr(2, 9)}`;
    const hasError = !!errorText;

    const sizeClasses = {
      sm: 'px-3 py-1.5 text-sm',
      md: 'px-3 py-2 text-sm',
      lg: 'px-4 py-3 text-base',
    };

    return (
      <div className={cn('relative', fullWidth && 'w-full')}>
        {label && (
          <label
            htmlFor={inputId}
            className="block text-sm font-medium mb-1"
            style={{ color: 'var(--color-text-primary)' }}
          >
            {label}
            {required && (
              <span className="ml-1" style={{ color: 'var(--color-error-500)' }}>
                *
              </span>
            )}
          </label>
        )}

        <div className="relative">
          {leftIcon && (
            <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
              {leftIcon}
            </div>
          )}

          <input
            ref={ref}
            id={inputId}
            type={type}
            className={cn(
              'w-full rounded-lg border transition-colors focus:outline-none focus:ring-1',
              sizeClasses[size],
              leftIcon && 'pl-10',
              rightIcon && 'pr-10',
              className
            )}
            style={{
              backgroundColor: 'var(--color-background-paper)',
              borderColor: hasError
                ? 'var(--color-error-500)'
                : 'var(--color-border-light)',
              color: 'var(--color-text-primary)',
              '--tw-ring-color': hasError
                ? 'var(--color-error-500)'
                : 'var(--color-primary-500)',
            } as React.CSSProperties}
            onFocus={(e) => {
              if (!hasError) {
                e.target.style.borderColor = 'var(--color-primary-500)';
              }
              onFocus?.(e);
            }}
            onBlur={(e) => {
              if (!hasError) {
                e.target.style.borderColor = 'var(--color-border-light)';
              }
              onBlur?.(e);
            }}
            onWheel={(e) => {
              // A focused number input treats wheel scroll as ±step, silently
              // nudging values (e.g. 3500 -> 3499.99). Blur so the page scrolls instead.
              if (type === 'number') {
                e.currentTarget.blur();
              }
              onWheel?.(e);
            }}
            aria-invalid={hasError}
            aria-describedby={
              hasError && errorText
                ? `${inputId}-error`
                : helperText
                  ? `${inputId}-helper`
                  : undefined
            }
            {...props}
          />

          {rightIcon && (
            <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
              {rightIcon}
            </div>
          )}
        </div>

        {hasError && errorText && (
          <p id={`${inputId}-error`} className="mt-1 text-xs" style={{ color: 'var(--color-error-500)' }}>
            {errorText}
          </p>
        )}

        {!hasError && helperText && (
          <p id={`${inputId}-helper`} className="mt-1 text-xs opacity-70" style={{ color: 'var(--color-text-secondary)' }}>
            {helperText}
          </p>
        )}
      </div>
    );
  }
);

Input.displayName = 'Input';

export { Input };

